import { toast } from "sonner";
import { extractApiError } from "./apiErrors";

export type ErrorCategory =
  | "network"
  | "auth"
  | "permission"
  | "validation"
  | "server"
  | "unknown";

export interface AppError {
  category: ErrorCategory;
  message: string; // User-facing message
  detail?: string; // Technical detail for console
  retryable: boolean;
}

/**
 * Categorize an unknown error into a structured AppError with a user-friendly
 * Spanish message, error category, and retryability flag.
 */
export function categorizeError(error: unknown): AppError {
  const apiError = extractApiError(error);

  // Network-level errors (no response received)
  if (error instanceof Error) {
    if (
      error.message.includes("Network Error") ||
      error.message.includes("ERR_NETWORK") ||
      error.message.includes("Failed to fetch") ||
      error.message.includes("ECONNABORTED") ||
      error.message.includes("timeout")
    ) {
      return {
        category: "network",
        message: apiError.message || "Sin conexión. Verifica tu internet.",
        detail: error.message,
        retryable: true,
      };
    }
  }

  if (apiError.status > 0) {
    if (apiError.status === 401) {
      return {
        category: "auth",
        message: apiError.message || "Sesión expirada. Inicia sesión de nuevo.",
        detail: apiError.detail,
        retryable: false,
      };
    }
    if (apiError.status === 403) {
      return {
        category: "permission",
        message: apiError.message || "No tienes permiso para esta acción.",
        detail: apiError.detail,
        retryable: false,
      };
    }
    if (apiError.status === 404) {
      return {
        category: "server",
        message: apiError.message || "Recurso no encontrado.",
        detail: apiError.detail,
        retryable: false,
      };
    }
    if (apiError.status === 422 || apiError.status === 400) {
      return {
        category: "validation",
        message: apiError.message || "Datos inválidos.",
        detail: apiError.detail,
        retryable: false,
      };
    }
    if (apiError.status === 429) {
      return {
        category: "server",
        message:
          apiError.message || "Demasiadas solicitudes. Espera un momento.",
        detail: apiError.detail,
        retryable: true,
      };
    }
    if (apiError.status >= 500) {
      return {
        category: "server",
        message: apiError.message || "Error del servidor. Intenta de nuevo.",
        detail: apiError.detail,
        retryable: true,
      };
    }
  }

  if (
    apiError.status === 0 &&
    apiError.message.includes("conectar con el servidor")
  ) {
    return {
      category: "network",
      message: apiError.message,
      detail: error instanceof Error ? error.message : apiError.detail,
      retryable: true,
    };
  }

  return {
    category: "unknown",
    message: apiError.message || "Algo salió mal. Intenta de nuevo.",
    detail: error instanceof Error ? error.message : String(error),
    retryable: true,
  };
}

/**
 * Show a categorized error toast with optional context prefix.
 * Also logs the full error to console for debugging.
 *
 * @param error - The caught error (unknown type)
 * @param context - Optional context string, e.g. "Guardar capa"
 */
export function showError(error: unknown, context?: string) {
  const appError = categorizeError(error);
  const prefix = context ? `${context}: ` : "";
  toast.error(`${prefix}${appError.message}`);
  console.error(`[${appError.category}] ${context || "Error"}:`, error);
  return appError;
}
